import type { Editor } from 'OpalChat/TiptapEditorTypes';
import { useMemo, useState } from 'react';

import { useSuggestionExtension } from '@frontend/components/TiptapEditor/hooks/usePromptTemplateSuggestion';
import {
  AgentFormPromptTemplate,
  InstructionsSuggestionList,
  ToolsSuggestionList,
  VariableSuggestionList,
} from '@frontend/components/TiptapEditor/SuggestionLists';
import type { AgentParameter, AgentParameterType } from '@frontend/types';

type UseAgentFormSuggestionsOptions = {
  parameters: Array<AgentParameter<AgentParameterType>>;
  agentToolNames: string[];
};

export function useAgentFormSuggestions({
  parameters,
  agentToolNames,
}: UseAgentFormSuggestionsOptions) {
  // State for parameter form modal (for SuggestionList integration)
  const [parameterFormOpen, setParameterFormOpen] = useState(false);
  const [editingParameter, setEditingParameter] = useState<
    AgentParameter<AgentParameterType> | undefined
  >(undefined);
  const [toolDialogOpen, setToolDialogOpen] = useState(false);

  const suggestionConfig = useMemo(
    () => ({
      variables: parameters,
      onCreateVariable: () => {
        setEditingParameter(undefined);
        setParameterFormOpen(true);
      },
      onEditVariable: (variable: AgentParameter<AgentParameterType>) => {
        setEditingParameter(variable);
        setParameterFormOpen(true);
      },
      onInsertVariable: (editor: Editor, variableName: string) => {
        editor.chain().focus().insertContent(`[[${variableName}]] `).run();
      },
      tools: agentToolNames,
      onAddTool: () => {
        setToolDialogOpen(true);
      },
      onEditTool: () => {
        setToolDialogOpen(true);
      },
      onInsertTool: (editor: Editor, toolName: string) => {
        editor.chain().focus().insertContent(`\`${toolName}\` `).run();
      },
      onInsertInstructions: (editor: Editor) => {
        editor.chain().focus().insertContent('{instruction: }').run();
        // Move cursor back 1 position (before the closing brace)
        const pos = editor.state.selection.anchor - 1;
        editor.commands.setTextSelection(pos);
      },
      onInsertRetrieval: (editor: Editor) => {
        editor.chain().focus().insertContent('{retrieval: }').run();
        const pos = editor.state.selection.anchor - 1;
        editor.commands.setTextSelection(pos);
      },
      onInsertRetrievalWithFiles: (editor: Editor) => {
        editor.chain().focus().insertContent('{retrieval+: }').run();
        const pos = editor.state.selection.anchor - 1;
        editor.commands.setTextSelection(pos);
      },
    }),
    [parameters, agentToolNames]
  );

  // / for all options
  const slashExtensions = useSuggestionExtension({
    name: 'slashAgentSuggestion',
    char: '/',
    config: suggestionConfig,
    component: AgentFormPromptTemplate,
  });

  // [ for variables
  const bracketExtensions = useSuggestionExtension({
    name: 'bracketVariableSuggestion',
    char: '[',
    config: suggestionConfig,
    component: VariableSuggestionList,
  });

  // { for instructions
  const curlyExtensions = useSuggestionExtension({
    name: 'curlyInstructionsSuggestion',
    char: '{',
    config: suggestionConfig,
    component: InstructionsSuggestionList,
  });

  // ` for tools
  const backtickExtensions = useSuggestionExtension({
    name: 'backtickToolsSuggestion',
    char: '`',
    config: suggestionConfig,
    component: ToolsSuggestionList,
  });

  // Combine all extensions
  const commandExtensions = useMemo(() => {
    const allExtensions = [
      ...(slashExtensions || []),
      ...(bracketExtensions || []),
      ...(curlyExtensions || []),
      ...(backtickExtensions || []),
    ];
    return allExtensions.length > 0 ? allExtensions : null;
  }, [slashExtensions, bracketExtensions, curlyExtensions, backtickExtensions]);

  return {
    commandExtensions,
    // Parameter form state
    parameterFormOpen,
    setParameterFormOpen,
    editingParameter,
    setEditingParameter,
    // Tool dialog state
    toolDialogOpen,
    setToolDialogOpen,
  };
}
